import Cart from "@theme/cart-store";
import { ThemeEvents, CartUpdateEvent } from "@theme/events";

/**
 * Format money value using Shopify money format template
 * @param {number} value - Money value in cents 
 * @param {string} format - Money format template (e.g., "")
 * @returns {string} Formatted money string
 */
function formatMoney(value, format) {
  // ES5-compatible default parameter
  if (format === undefined) {
    format = '{{amount}}';
  }
  if (typeof value !== 'number' || isNaN(value)) {
    return '';
  }

  // Convert cents to dollars
  const dollars = value / 100;
  
  // Get currency from window.theme if available (ES5-compatible optional chaining)
  const currency = (window.theme && window.theme.currency) || 'USD';
  
  // Currency decimal places
  const CURRENCY_DECIMALS = {
    'JPY': 0,
    'KRW': 0,
    'VND': 0,
    'CLP': 0,
  };
  const DEFAULT_CURRENCY_DECIMALS = 2;
  
  // Format the amount using the template
  // Build regex pattern dynamically to avoid Liquid processing 
  const templateRegex = new RegExp('\\{\\{\\s*(\\w+)\\s*\\}\\}', 'g');
  const formatted = format.replace(templateRegex, (_, placeholder) => {
    if (placeholder === 'currency') {
      return currency;
    }
    
    // Determine precision and separators based on placeholder
    let thousandsSeparator = ',';
    let decimalSeparator = '.';
    const currencyKey = currency.toUpperCase();
    let precision = (CURRENCY_DECIMALS[currencyKey] !== undefined) ? CURRENCY_DECIMALS[currencyKey] : DEFAULT_CURRENCY_DECIMALS;
    
    if (placeholder === 'amount') {
      // Use defaults
    } else if (placeholder === 'amount_no_decimals') {
      precision = 0;
    } else if (placeholder === 'amount_with_comma_separator') {
      thousandsSeparator = '.';
      decimalSeparator = ',';
    } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
      thousandsSeparator = '.';
      precision = 0;
    } else if (placeholder === 'amount_no_decimals_with_space_separator') {
      thousandsSeparator = ' ';
      precision = 0;
    } else if (placeholder === 'amount_with_space_separator') {
      thousandsSeparator = ' ';
      decimalSeparator = ',';
    } else if (placeholder === 'amount_with_period_and_space_separator') {
      thousandsSeparator = ' ';
      decimalSeparator = '.';
    } else if (placeholder === 'amount_with_apostrophe_separator') {
      thousandsSeparator = "'";
      decimalSeparator = '.';
    }
    
    // Format the cents value
    const roundedNumber = dollars.toFixed(precision);
    const parts = roundedNumber.split('.');
    let a = parts[0] || '0';
    let b = parts[1] || '';
    
    // Add thousands separator
    a = a.replace(/\d(?=(\d\d\d)+(?!\d))/g, function(digit) {
      return digit + thousandsSeparator;
    });
    
    return precision <= 0 ? a : a + decimalSeparator + b.padEnd(precision, '0');
  });
  
  return formatted;
}

class FreeGiftProgressBar extends HTMLElement {
    #playingAnimation = false;
    #parentModal = null;
    #isProcessingGift = false;
    #lastGiftCheck = 0;
    #pendingGiftPayload = null;
    
    constructor() {
        super();
    }
    
    async connectedCallback() {
        try {
            this.amountToSpendElement = this.querySelector("amount-to-spend");
            this.amountLeftElement = this.querySelector("amount-left");
            this.moneyValueLeftElement = this.amountLeftElement?.querySelector("money-value");
            this.minimumReachedElement = this.querySelector("minimum-reached");
            this.track = this.querySelector("free-gift-progress-bar-track");
            this.goalAnimation = null;
            this.#playingAnimation = false;
            
            // Read milestone attributes and build milestones from Liquid settings
            this.milestones = this.#buildMilestonesFromSettings();
            this.currentMilestone = null;
            this.nextMilestone = null;
            this.progressPercentage = Number(this.getAttribute("progress-percentage")) || 0;
            
            // Legacy support for backward compatibility
            this.minimumValue = Number(this.getAttribute("minimum-value")) || 0;
            this.value = Number(this.getAttribute("value")) || 0;
            this.giftProductId = this.getAttribute("gift-product-id");
            
            this.#parentModal = this.closest("modal-dialog, popup-dialog");
            this.lastCartItemCount = 0;

            // Parse excluded product IDs injected by Liquid (comma-separated string)
            const excludedAttr = this.getAttribute('data-excluded-product-ids') || '';
            this.excludedProductIds = new Set(
                excludedAttr.split(',').map(id => id.trim()).filter(Boolean)
            );

            // Property key whose presence on a line item marks it as excluded
            this.excludedPropertyKey = this.getAttribute('data-excluded-property-key') || '';
            
            // Validate required elements
            if (!this.track) {
                console.error('FreeGiftProgressBar: Track element not found');
                return;
            }
            
            if (!this.amountToSpendElement || !this.amountLeftElement || !this.minimumReachedElement) {
                console.error('FreeGiftProgressBar: Required text elements not found');
                return;
            }
            
            // Validate cart store
            if (!Cart || typeof Cart.subscribe !== 'function') {
                console.error('FreeGiftProgressBar: Cart store not properly initialized');
                return;
            }
            
            this.#setVariables();
            
            // Subscribe to cart changes
            this.unsubscribe = Cart.subscribe(async (state) => {
                if (!state.cart) {
                    return;
                }
                
                const newValue = this.#calculateCartTotalExcludingGiftCards(state.cart);
                const valueChanged = this.value !== newValue;
                this.value = newValue;
                this.#updateMilestones();
                this.#update();
                await this.#handleGiftProduct(state.cart, newValue, valueChanged);
                
                // Check if minimum is reached for animation
                if (this.value >= this.minimumValue && this.track) {
                    this.track.addEventListener("transitionend", () => {
                        setTimeout(() => {
                            this.#playGoalAnimation();
                        }, 250);
                    }, {
                        once: true
                    });
                }
            });
            
            this.revealDelay = this.getAttribute("reveal-delay") || 0;
            if (this.#parentModal) {
                this.#parentModal.addEventListener("modal:opened", () => {
                    setTimeout(() => {
                        this.classList.add("is-visible");
                    }, this.revealDelay);
                });
            } else {
                setTimeout(() => {
                    this.classList.add("is-visible");
                }, this.revealDelay);
            }
            
            
        } catch (error) {
            // Silent error handling for production
        }
    }
    
    disconnectedCallback() {
        if (this.unsubscribe) {
            this.unsubscribe();
        }
    }
    
    #calculateCartTotalExcludingGiftCards(cart) {
        // Hardcoded fallback for the gift card product (in case it's not in theme settings)
        const GIFT_CARD_PRODUCT_ID = '7549346545814';

        let total = 0;
        for (const item of cart.items) {
            const productId = item.product_id.toString();
            if (
                productId === GIFT_CARD_PRODUCT_ID ||
                this.excludedProductIds.has(productId) ||
                (this.excludedPropertyKey && item.properties && item.properties[this.excludedPropertyKey] != null) ||
                (item.properties && item.properties._free_gift === 'true')
            ) {
                continue;
            }
            total += item.final_line_price;
        }
        return total;
    }
    
    #buildMilestonesFromSettings() {
        const milestones = [];
        
        
        
            
            // Milestone 1
            
                milestones.push({
                    threshold: 8000,
                    reward_type: 'free_shipping',
                    reward_data: {
                        title: 'Reduced shipping at $80',
                        icon: '🚚',
                        achieved_text: 'You earned reduced shipping!'
                    }
                });
            
            
            // Milestone 2
            
                milestones.push({
                    threshold: 18000,
                    reward_type: 'free_gift',
                    reward_data: {
                        title: 'Free Gift!',
                        icon: '🧀',
                        achieved_text: 'You have earned a free gift!',
                        product_handle: 'balsamic-fig-walnut-fromage-free-gift-small-7-oz'
                    }
                });
            
            
            // Milestone 3
            
                milestones.push({
                    threshold: 23000,
                    reward_type: 'free_gift',
                    reward_data: {
                        title: 'Free Gift',
                        icon: '🧀',
                        achieved_text: 'You have earned a free gift!',
                        product_handle: 'black-truffle-pecorino-free-gift-small-5-oz'
                    }
                });
            
            
            // Milestone 4
            
        
        
        return milestones;
    }
    
    async #getVariantIdFromHandle(productHandle) {
        if (!productHandle) return null;
        
        try {
            // Try to get the product data from the current page or use a fallback
            const productUrl = `/products/${productHandle}.js`;
            const response = await fetch(productUrl);
            
            if (!response.ok) {
                return null;
            }
            
            const product = await response.json();
            
            // Get the first available variant ID
            if (product.variants && product.variants.length > 0) {
                return product.variants[0].id;
            }
            
            return null;
        } catch (error) {
            return null;
        }
    }
    
    #updateMilestones() {
        if (!this.milestones || this.milestones.length === 0) {
            return;
        }
        
        // Find current and next milestones based on cart value
        let currentMilestone = null;
        let nextMilestone = null;
        
        for (const milestone of this.milestones) {
            if (this.value >= milestone.threshold) {
                currentMilestone = milestone;
            } else {
                if (!nextMilestone || milestone.threshold < nextMilestone.threshold) {
                    nextMilestone = milestone;
                }
            }
        }
        
        // Calculate overall progress through all milestones
        // This ensures the progress bar shows cumulative progress, not just current milestone progress
        const highestThreshold = Math.max(...this.milestones.map(m => m.threshold));
        let progressPercentage = 0;
        
        if (highestThreshold > 0) {
            progressPercentage = Math.min(100, Math.max(0, (this.value / highestThreshold) * 100));
        }
        
        this.currentMilestone = currentMilestone;
        this.nextMilestone = nextMilestone;
        this.progressPercentage = progressPercentage;
    }
    
    #setVariables() {
        // Re-query track element in case it was replaced during morphing
        if (!this.track || !this.isConnected) {
            this.track = this.querySelector("free-gift-progress-bar-track");
        }
        
        // Ensure track element exists
        if (!this.track) {
            console.warn('FreeGiftProgressBar: Track element not found in setVariables');
            return;
        }
        
        // Use milestone-based progress if available, otherwise fall back to legacy logic
        if (this.milestones && this.milestones.length > 0) {
            const displacement = 100 - this.progressPercentage;
            this.track.style.setProperty("--displacement", `${displacement}%`);
            if (this.style) {
                this.style.setProperty("--displacement", `${displacement}%`);
            }
        } else {
            // Legacy fallback
            const moneyLeft = Math.max(0, this.minimumValue - this.value);
            const percentLeft = (moneyLeft / this.minimumValue) * 100;
            const displacement = Math.round(percentLeft);
            this.track.style.setProperty("--displacement", `${displacement}%`);
            if (this.style) {
                this.style.setProperty("--displacement", `${displacement}%`);
            }
        }
    }
    
    #update() {
        try {
            this.#setVariables();
            
            // Update display based on milestones
            if (this.milestones && this.milestones.length > 0) {
                this.#updateMilestoneDisplay();
            } else {
                // Legacy fallback
                this.#updateLegacyDisplay();
            }
            
            // Update milestone achieved text
            this.#updateMilestoneAchievedText();
            
        } catch (error) {
            // Silent error handling for production
        }
    }
    
    #updateMilestoneDisplay() {
        // Show/hide elements based on milestone state
        if (this.nextMilestone) {
            this.amountToSpendElement.hidden = this.value > 0;
            this.amountLeftElement.hidden = this.value === 0 || this.progressPercentage === 100;
        } else {
            this.amountToSpendElement.hidden = true;
            this.amountLeftElement.hidden = true;
        }
        
        if (this.currentMilestone) {
            this.minimumReachedElement.hidden = this.progressPercentage < 100;
        } else {
            this.minimumReachedElement.hidden = true;
        }
        
        // Update milestone markers
        this.#updateMilestoneMarkers();
    }
    
    #updateMilestoneMarkers() {
        if (!this.track || !this.milestones || this.milestones.length === 0) return;
        
        // Remove existing markers
        const existingMarkers = this.track.querySelectorAll('.milestone-marker');
        existingMarkers.forEach(marker => marker.remove());
        
        // Find the highest threshold for positioning
        const highestThreshold = Math.max(...this.milestones.map(m => m.threshold));
        
        // Add markers for each milestone
        this.milestones.forEach(milestone => {
            if (milestone.threshold > 0) {
                const marker = document.createElement('div');
                marker.className = 'milestone-marker';
                marker.style.cssText = `
                    position: absolute;
                    left: ${(milestone.threshold / highestThreshold) * 100}%;
                    top: 0;
                    height: 100%;
                    width: 2px;
                    background-color: rgba(255, 255, 255, 0.5);
                    z-index: 2;
                `;
                
                const icon = document.createElement('span');
                icon.className = 'milestone-marker__icon';
                icon.textContent = milestone.reward_data.icon;
                icon.style.cssText = `
                    position: absolute;
                    top: -20px;
                    left: -10px;
                    font-size: 12px;
                    background-color: var(--track-color, #007cba);
                    color: white;
                    padding: 2px 4px;
                    border-radius: 4px;
                    white-space: nowrap;
                `;
                
                const threshold = document.createElement('span');
                threshold.className = 'milestone-marker__threshold';
                threshold.textContent = `$${(milestone.threshold / 100).toFixed(0)}`;
                threshold.style.cssText = `
                    position: absolute;
                    bottom: -20px;
                    left: -20px;
                    font-size: 10px;
                    color: var(--track-color, #007cba);
                    white-space: nowrap;
                `;
                
                marker.appendChild(icon);
                marker.appendChild(threshold);
                this.track.appendChild(marker);
            }
        });
    }
    
    #updateLegacyDisplay() {
        const moneyLeft = Math.max(0, this.minimumValue - this.value);
        
        // Ensure we have a valid money format and valid number
        const moneyFormat = window.theme?.money_format || '';
        const validMoneyLeft = isNaN(moneyLeft) ? 0 : moneyLeft;
        const moneyLeftFormatted = formatMoney(validMoneyLeft, moneyFormat);
        
        if (this.moneyValueLeftElement) {
            this.moneyValueLeftElement.textContent = moneyLeftFormatted;
        }
        
        if (this.value === 0) {
            this.amountToSpendElement.hidden = false;
            this.amountLeftElement.hidden = true;
            this.minimumReachedElement.hidden = true;
        } else if (moneyLeft > 0) {
            this.amountToSpendElement.hidden = true;
            this.amountLeftElement.hidden = false;
            this.minimumReachedElement.hidden = true;
        } else {
            this.amountToSpendElement.hidden = true;
            this.amountLeftElement.hidden = true;
            this.minimumReachedElement.hidden = false;
        }
    }
    
    #updateMilestoneAchievedText() {
        const milestoneTextEl = this.querySelector('.free-gift-progress-bar__milestone-achieved');
        if (!milestoneTextEl) return;

        milestoneTextEl.textContent = '';

        if (!this.milestones || this.milestones.length === 0) {
            if (this.progressPercentage >= 100) {
                milestoneTextEl.textContent = '🎉 Free gift unlocked!';
            }
            return;
        }

        let achievedMilestone = null;
        for (const milestone of this.milestones) {
            if (this.value >= milestone.threshold && milestone.reward_data.achieved_text) {
                achievedMilestone = milestone;
            }
        }

        if (achievedMilestone) {
            milestoneTextEl.textContent = achievedMilestone.reward_data.achieved_text;
        }
    }
    
    async #handleGiftProduct(cart, cartSubtotal, valueChanged) {
        // Prevent race conditions and excessive calls
        const now = Date.now();
        if (this.#isProcessingGift) {
            if (valueChanged) {
                this.#pendingGiftPayload = { cart, cartSubtotal, valueChanged };
            }
            return;
        }

        if (!valueChanged && (now - this.#lastGiftCheck) < 500) {
            return;
        }
        
        this.#lastGiftCheck = now;
        this.#isProcessingGift = true;
        
        try {
            if (cartSubtotal === undefined || cartSubtotal === null) {
                cartSubtotal = this.#calculateCartTotalExcludingGiftCards(cart);
            }
            
            if (this.milestones && this.milestones.length > 0) {
                // Handle milestone-based gift products
                await this.#handleMilestoneGifts(cart, cartSubtotal);
            } else if (this.giftProductId) {
                // Legacy single gift product handling
                this.#handleLegacyGift(cart, cartSubtotal);
            }
        } finally {
            // Reset processing flag after a delay to allow for async operations
            setTimeout(() => {
                this.#isProcessingGift = false;
                if (this.#pendingGiftPayload) {
                    const pending = this.#pendingGiftPayload;
                    this.#pendingGiftPayload = null;
                    this.#handleGiftProduct(pending.cart, pending.cartSubtotal, pending.valueChanged);
                }
            }, 200);
        }
    }
    
    async #handleMilestoneGifts(cart, cartSubtotal) {
        
        // Get all gift milestones that should be active
        const activeGiftMilestones = this.milestones.filter(milestone => 
            milestone.reward_type === 'free_gift' && 
            cartSubtotal >= milestone.threshold &&
            milestone.reward_data.product_handle
        );

        // Build a fast lookup of current free-gift variants in cart.
        // This is updated as we add gifts to prevent duplicate adds in the same run.
        const existingGiftVariantIds = new Set(
            cart.items
                .filter(item => item.properties && item.properties._free_gift === 'true')
                .map(item => item.variant_id.toString())
        );
        
        // Convert product handles to variant IDs and add missing gifts
        for (const milestone of activeGiftMilestones) {
            const productHandle = milestone.reward_data.product_handle;
            const variantId = await this.#getVariantIdFromHandle(productHandle);
            
            if (!variantId) {
                continue;
            }
            
            const variantIdString = variantId.toString();
            const hasGiftInCart = existingGiftVariantIds.has(variantIdString);
            
            if (!hasGiftInCart) {
                await this.#addGiftToCart(variantId);
                existingGiftVariantIds.add(variantIdString);
            }
        }
        
        // Remove gifts that are no longer eligible
        
        // Only identify items with _free_gift property as free gifts
        // This prevents purchased products (same variant, no property) from being removed
        const cartGiftItems = cart.items.filter(item => {
            return item.properties && item.properties._free_gift === 'true';
        });
        
        // Normalize any free-gift quantity drift (e.g., duplicate add race) back to 1.
        for (const cartItem of cartGiftItems) {
            if (cartItem.quantity > 1) {
                await this.#setGiftQuantity(cartItem.key, 1);
            }
        }
        
        
        // Collect all gifts that need to be removed
        const giftsToRemove = [];
        
        for (const cartItem of cartGiftItems) {
            let shouldRemoveGift = false;
            let milestoneInfo = null;
            
            // Check each milestone to see if this gift is still eligible
            for (const milestone of this.milestones) {
                if (milestone.reward_type !== 'free_gift' || !milestone.reward_data.product_handle) {
                    continue;
                }
                
                // Convert product handle to variant ID for comparison
                const variantId = await this.#getVariantIdFromHandle(milestone.reward_data.product_handle);
                
                if (variantId && variantId.toString() === cartItem.variant_id.toString()) {
                    // This gift belongs to this milestone
                    milestoneInfo = milestone;
                    
                    // Check if the milestone is still active
                    if (cartSubtotal < milestone.threshold) {
                        shouldRemoveGift = true;
                    }
                    break; // Found the milestone, no need to check others
                }
            }
            
            // Add to removal list if no longer eligible
            if (shouldRemoveGift && milestoneInfo) {
                giftsToRemove.push(cartItem.key);
            }
        }
        
        // Remove all ineligible gifts in a single batch operation
        if (giftsToRemove.length > 0) {
            await this.#batchRemoveGiftsFromCart(giftsToRemove);
        }
    }
    
    #handleLegacyGift(cart, cartSubtotal) {
        const hasGiftInCart = cart.items.some(item => item.variant_id.toString() === this.giftProductId);
        const shouldHaveGift = cartSubtotal >= this.minimumValue;
        
        // Remove gift if cart total is below threshold and gift is in cart
        if (!shouldHaveGift && hasGiftInCart) {
            const giftItem = cart.items.find(item => item.variant_id.toString() === this.giftProductId);
            if (giftItem) {
                this.#removeGiftFromCart(giftItem.key);
            }
        }
        
        // Add gift if cart total meets threshold and gift is not in cart
        if (shouldHaveGift && !hasGiftInCart) {
            this.#addGiftToCart(this.giftProductId);
        }
    }
    
    async #addGiftToCart(productId) {
        try {
            
            const response = await fetch('/cart/add.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    id: productId,
                    quantity: 1,
                    properties: {
                        '_free_gift': 'true'
                    }
                })
            });
            
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(`Failed to add gift to cart: ${response.status} ${errorData.description || response.statusText}`);
            }
            
            await response.json();
            
            // Set internal refresh flag to ensure Cart.refresh() dispatches event
            if (Cart._isInternalRefresh !== undefined) {
                Cart._isInternalRefresh = true;
            }
            
            // Trigger cart refresh to update state
            await Cart.refresh();
            
            // Get the updated cart from Cart store to dispatch event
            const cartState = Cart.getState();
            const updatedCart = cartState && cartState.cart;
            if (updatedCart) {
                // Manually dispatch CartUpdateEvent to ensure all UI components update
                document.dispatchEvent(
                    new CartUpdateEvent(updatedCart, 'free-gift-progress-bar', {
                        source: 'free-gift-progress-bar',
                        itemCount: updatedCart.item_count
                    })
                );
            }
        } catch (error) {
            // Silent error handling for production
        }
    }
    
    async #removeGiftFromCart(itemKey) {
        try {
            
            const response = await fetch('/cart/change.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    id: itemKey,
                    quantity: 0
                })
            });
            
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(`Failed to remove gift from cart: ${response.status} ${errorData.description || response.statusText}`);
            }
            
            await response.json();
            
            // Set internal refresh flag to ensure Cart.refresh() dispatches event
            if (Cart._isInternalRefresh !== undefined) {
                Cart._isInternalRefresh = true;
            }
            
            // Trigger cart refresh to update state
            await Cart.refresh();
            
            // Get the updated cart from Cart store to dispatch event
            const cartState = Cart.getState();
            const updatedCart = cartState && cartState.cart;
            if (updatedCart) {
                // Manually dispatch CartUpdateEvent to ensure all UI components update
                document.dispatchEvent(
                    new CartUpdateEvent(updatedCart, 'free-gift-progress-bar', {
                        source: 'free-gift-progress-bar',
                        itemCount: updatedCart.item_count
                    })
                );
            }
        } catch (error) {
            // Silent error handling for production
        }
    }

    async #setGiftQuantity(itemKey, quantity) {
        try {
            const response = await fetch('/cart/change.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    id: itemKey,
                    quantity
                })
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(`Failed to set gift quantity: ${response.status} ${errorData.description || response.statusText}`);
            }

            await response.json();
        } catch (error) {
            // Silent error handling for production
        }
    }
    
    async #batchRemoveGiftsFromCart(itemKeys) {
        try {
            // Build updates object: { 'item_key_1': 0, 'item_key_2': 0, ... }
            const updates = {};
            itemKeys.forEach(key => {
                updates[key] = 0;
            });
            
            
            const response = await fetch('/cart/update.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ updates })
            });
            
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(`Failed to batch remove gifts from cart: ${response.status} ${errorData.description || response.statusText}`);
            }
            
            await response.json();
            
            // Set internal refresh flag to ensure Cart.refresh() dispatches event
            if (Cart._isInternalRefresh !== undefined) {
                Cart._isInternalRefresh = true;
            }
            
            // Trigger cart refresh once after all removals
            await Cart.refresh();
            
            // Get the updated cart from Cart store to dispatch event
            const cartState = Cart.getState();
            const updatedCart = cartState && cartState.cart;
            if (updatedCart) {
                // Manually dispatch CartUpdateEvent to ensure all UI components update
                document.dispatchEvent(
                    new CartUpdateEvent(updatedCart, 'free-gift-progress-bar', {
                        source: 'free-gift-progress-bar',
                        itemCount: updatedCart.item_count
                    })
                );
            }
        } catch (error) {
            // Silent error handling for production
        }
    }
    
    async #playGoalAnimation() {
        if (this.#playingAnimation) return;
        this.#playingAnimation = true;
        this.goalAnimation = this.querySelector("free-gift-progress-bar-goal-animation");
        if (this.goalAnimation) {
            try {
                const { default: confetti } = await import("https://cdn.skypack.dev/canvas-confetti");
                const canvas = this.goalAnimation.querySelector("canvas");
                const myConfetti = confetti.create(canvas, {
                    resize: true,
                    useWorker: true
                });
                myConfetti({
                    particleCount: 100,
                    spread: 70,
                    origin: { y: 0.6 }
                });
                setTimeout(() => {
                    myConfetti.reset();
                    this.#playingAnimation = false;
                }, 3000);
            } catch (error) {
                this.#playingAnimation = false;
            }
        } else {
            this.#playingAnimation = false;
        }
    }

    
    // Create progress fill element for fallback
    createProgressFill() {
        if (this.track.querySelector('.progress-fill')) {
            return this.track.querySelector('.progress-fill');
        }
        
        const progressFill = document.createElement('div');
        progressFill.className = 'progress-fill';
        progressFill.style.cssText = `
            position: absolute;
            top: 0;
            left: 0;
            height: 100%;
            background-color: var(--track-color, #007cba);
            transition: width 0.3s ease;
            z-index: 1;
        `;
        
        this.track.appendChild(progressFill);
        return progressFill;
    }
}

customElements.define("free-gift-progress-bar", FreeGiftProgressBar);
